import React, { useEffect, useState } from 'react';
import { FormationsTertiaryButton } from 'components/common/Buttons';
import { Grid, Typography } from '@mui/material';
import { queryClient } from 'states/reactQueryClient';
import { homeAddressSchema } from 'schemas/homeAddressSchema';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  FormationsFormActions,
  FormationsFormContainer,
  FormationsFormContent,
  FormationsFormFields,
  FormationsFormItem,
  IFormField,
  renderField,
} from 'components/common/FormationsForm2';
import { formatZipCode } from 'components/common/FormationsForm2/FormationsTextField';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCurrentCompany } from 'hooks/api';
import { ProgressTrackerStatus } from 'models/account';
import { ReadOnlyForm } from 'components/common/ReadOnlyForm';
import { isPhysicalSameAsMailing } from 'components/AdminProgressTracker/Incorporation/BusinessAddress';
import { Address } from 'models/business';
import { useCurrentHousehold, useUpdateHousehold } from 'hooks/api/useHousehold';
import { SubtitleText } from 'components/ProgressTracker/StyledTypography';
import { StepInputContentContainer } from '../Components/StepInputContentContainer';
import { STATES } from '../../../enums';

interface TParams {
  handleContinue: () => void;
  currentStatus: ProgressTrackerStatus;
}

interface IFormData {
  homeAddress1: string;
  homeAddress2: string;
  homeCity: string;
  homeState: string;
  homeZip: string;
}

const defaultAddress = {
  homeSameAsBusiness: false,
  homeSameAsMailing: false,
  homeAddress1: '',
  homeAddress2: '',
  homeCity: '',
  homeState: '',
  homeZip: '',
};

export const HomeAddress = ({ handleContinue, currentStatus }: TParams) => {
  const { t } = useTranslation();
  const { currentCompany } = useCurrentCompany();
  const { currentHousehold } = useCurrentHousehold();

  const mapState = (state: { name: string; code: string }) => ({
    name: state.name,
    value: state.code,
  });

  const isPhysicalAddressSameAsMailing = isPhysicalSameAsMailing(
    currentCompany?.mailingAddress,
    currentCompany?.businessAddress,
  );

  const formInstance = useForm({
    ...defaultAddress,
    resolver: yupResolver(homeAddressSchema),
  });

  const homeSameAsBusiness = formInstance.watch('homeSameAsBusiness');
  const homeSameAsMailing = formInstance.watch('homeSameAsMailing');
  const homeSameAsBoth = formInstance.watch('homeSameAsBoth');
  const homeAddress1 = formInstance.watch('homeAddress1');
  const homeAddress2 = formInstance.watch('homeAddress2');
  const homeCity = formInstance.watch('homeCity');
  const homeState = formInstance.watch('homeState');
  const homeZip = formInstance.watch('homeZip');
  const [
    userHasTypedAfterCheckingMailingBox,
    setUserHasTypedAfterCheckingMailingBox,
  ] = useState(false);
  const [
    userHasTypedAfterCheckingBusinessBox,
    setUserHasTypedAfterCheckingBusinessBox,
  ] = useState(false);
  const [
    userHasTypedAfterCheckingBothBox,
    setUserHasTypedAfterCheckingBothBox,
  ] = useState(false);

  const formFields: IFormField[] = [
    ...(isPhysicalAddressSameAsMailing
      ? [
          {
            type: FormationsFormFields.CheckboxField,
            name: 'homeSameAsBoth',
            label: t('progressTracker.incorporation.homeSameAsBoth'),
            options: {
              placeholder: '',
            },
            disabled: !currentCompany?.businessAddress?.street1,
          },
        ]
      : [
          {
            type: FormationsFormFields.CheckboxField,
            name: 'homeSameAsBusiness',
            label: t('progressTracker.incorporation.homeSameAsBusiness'),
            options: {
              placeholder: '',
            },
            disabled: !currentCompany?.businessAddress?.street1,
          },
          {
            type: FormationsFormFields.CheckboxField,
            name: 'homeSameAsMailing',
            label: t('progressTracker.incorporation.homeSameAsMailing'),
            options: {
              placeholder: '',
            },
            disabled: !currentCompany?.mailingAddress?.street1,
          },
        ]),
    {
      type: FormationsFormFields.Text,
      name: 'homeAddress1',
      label: 'Address Line 1',
      options: {
        placeholder: '1234 Ave E',
        maxLength: 25,
      },
    },
    {
      type: FormationsFormFields.Text,
      name: 'homeAddress2',
      label: 'Address Line 2',
      options: {
        placeholder: '',
        maxLength: 250,
      },
    },
    {
      type: FormationsFormFields.Text,
      name: 'homeCity',
      label: 'City',
      options: {
        placeholder: 'Seattle',
        maxLength: 50,
      },
    },
    {
      type: FormationsFormFields.AutoComplete,
      name: 'homeState',
      label: 'State',
      options: {
        minWidth: '80px',
        maxWidth: '232px',
        placeholder: 'Washington',
        options: STATES.map(mapState),
      },
    },
    {
      type: FormationsFormFields.Text,
      name: 'homeZip',
      label: 'Zipcode',
      options: {
        placeholder: '12345',
        formatInput: formatZipCode,
      },
    },
  ];

  useEffect(() => {
    if (homeSameAsBoth === true) {
      formInstance.reset({
        homeSameAsBoth: true,
        homeAddress1: currentCompany?.businessAddress?.street1,
        homeAddress2: currentCompany?.businessAddress?.street2,
        homeCity: currentCompany?.businessAddress?.city,
        homeState: currentCompany?.businessAddress?.state,
        homeZip: currentCompany?.businessAddress?.zip,
      });
    }
  }, [homeSameAsBoth]);

  useEffect(() => {
    if (homeSameAsBusiness === true) {
      formInstance.setValue('homeSameAsMailing', false);
      formInstance.reset({
        homeSameAsBusiness: true,
        homeSameAsMailing: false,
        homeAddress1: currentCompany?.businessAddress?.street1,
        homeAddress2: currentCompany?.businessAddress?.street2,
        homeCity: currentCompany?.businessAddress?.city,
        homeState: currentCompany?.businessAddress?.state,
        homeZip: currentCompany?.businessAddress?.zip,
      });
    }
  }, [homeSameAsBusiness]);

  useEffect(() => {
    if (homeSameAsMailing === true) {
      formInstance.setValue('homeSameAsBusiness', false);
      formInstance.reset({
        homeSameAsBusiness: false,
        homeSameAsMailing: true,
        homeAddress1: currentCompany?.mailingAddress?.street1,
        homeAddress2: currentCompany?.mailingAddress?.street2,
        homeCity: currentCompany?.mailingAddress?.city,
        homeState: currentCompany?.mailingAddress?.state,
        homeZip: currentCompany?.mailingAddress?.zip,
      });
    }
  }, [homeSameAsMailing]);

  useEffect(() => {
    if (
      homeSameAsBoth === false &&
      userHasTypedAfterCheckingBothBox === false
    ) {
      formInstance.reset({
        ...defaultAddress,
      });
    }
  }, [homeSameAsBoth]);

  useEffect(() => {
    if (
      homeSameAsBusiness === false &&
      homeSameAsMailing === false &&
      (userHasTypedAfterCheckingMailingBox === false ||
        userHasTypedAfterCheckingBusinessBox === false)
    ) {
      formInstance.reset({
        ...defaultAddress,
      });
    }
  }, [homeSameAsBusiness, homeSameAsMailing]);

  const checkAddress = (
    address: Address | undefined,
    setUserHasTyped: React.Dispatch<React.SetStateAction<boolean>>,
    setSameAs: string,
  ) => {
    if (
      homeAddress1 !== address?.street1 ||
      homeAddress2 !== address?.street2 ||
      homeCity !== address?.city ||
      homeState !== address?.state ||
      homeZip !== address?.zip
    ) {
      setUserHasTyped(true);
      formInstance.setValue(setSameAs, false);
    } else if (
      homeAddress1 === address?.street1 &&
      homeAddress2 === address?.street2 &&
      homeCity === address?.city &&
      homeState === address?.state &&
      homeZip === address?.zip
    ) {
      setUserHasTyped(false);
      formInstance.setValue(setSameAs, true);
    }
  };

  useEffect(() => {
    checkAddress(
      currentCompany?.businessAddress,
      setUserHasTypedAfterCheckingBothBox,
      'homeSameAsBoth',
    );
    checkAddress(
      currentCompany?.businessAddress,
      setUserHasTypedAfterCheckingBusinessBox,
      'homeSameAsBusiness',
    );
    checkAddress(
      currentCompany?.mailingAddress,
      setUserHasTypedAfterCheckingMailingBox,
      'homeSameAsMailing',
    );
  }, [homeAddress1, homeAddress2, homeCity, homeState, homeZip]);

  const { updateHouseholdAsync, isLoading } = useUpdateHousehold({
    onSuccess: async () => {
      await queryClient.invalidateQueries(['currentHousehold']);
    },
  });

  const onFormSubmit = async (formData: IFormData) => {
    const homeAddress: Address = {
      street1: formData.homeAddress1,
      street2: formData.homeAddress2,
      city: formData.homeCity,
      state: formData.homeState,
      zip: formData.homeZip,
    };
    await updateHouseholdAsync({
      id: currentHousehold?.id ?? '',
      data: {
        homeAddress,
      }
    });
    handleContinue();
  };

  return (
    <div>
      <Typography variant="h5B" component="h5" data-test-id="address-details">
        Home Address
      </Typography>
      {currentStatus === ProgressTrackerStatus.Completed && (
        <ReadOnlyForm
          items={[
            {
              key: 'Street',
              title: 'Street',
              value: currentHousehold?.homeAddress?.street1
                ? `${currentHousehold?.homeAddress?.street1} ${currentHousehold?.homeAddress?.street2}`
                : 'N/A',
            },
            {
              key: 'City',
              title: 'City',
              value: currentHousehold?.homeAddress?.city ?? 'N/A',
            },
            {
              key: 'State',
              title: 'State',
              value: currentHousehold?.homeAddress?.state ?? 'N/A',
            },
            {
              key: 'Zipcode',
              title: 'Zipcode',
              value: currentHousehold?.homeAddress?.zip ?? 'N/A',
            },
          ]}
        />
      )}
      {currentStatus !== ProgressTrackerStatus.Completed && (
        <>
          <SubtitleText variant="body1">
            {t('progressTracker.incorporation.homeAddressSubtitle')}
          </SubtitleText>
          <FormationsFormContainer>
            <form onSubmit={formInstance.handleSubmit(onFormSubmit)}>
              <FormationsFormContent>
                <StepInputContentContainer>
                  <Grid container spacing={2} marginLeft={0.25}>
                    {formFields.map((field, index) => (
                      <Grid item xs={index < 2 ? 12 : 6} key={field.name}>
                        <FormationsFormItem key={field.name}>
                          {renderField(field, formInstance)}
                        </FormationsFormItem>
                      </Grid>
                    ))}
                  </Grid>
                </StepInputContentContainer>
              </FormationsFormContent>
              <FormationsFormActions>
                <FormationsTertiaryButton
                  isLoading={isLoading}
                  type="submit"
                  size="large"
                  data-testid="save-address"
                  loadingPosition="center"
                >
                  Save and Continue
                </FormationsTertiaryButton>
              </FormationsFormActions>
            </form>
          </FormationsFormContainer>
        </>
      )}
    </div>
  );
};
